class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    const summary = this.mainDetailsToggle.querySelector("summary");
    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
      if (summary) {
        summary.setAttribute("aria-expanded", "true");
      }
      this.mainDetailsToggle.setAttribute("aria-expanded", "true");
    } else {
      this.animations.forEach((animation) => animation.cancel());
      if (summary) {
        summary.setAttribute("aria-expanded", "false");
      }
      this.mainDetailsToggle.setAttribute("aria-expanded", "false");
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    this.mainDetailsToggle.setAttribute("aria-expanded", "false");
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  // Static property to track which instance is managing scroll state
  static activeScrollManager = null;

  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
    this.isAnimatingClose = false;
    this.isMenuOpen = false;
    this.shouldAnimateClose = false;

    // Override the summary click to control opening/closing manually
    const summary = this.mainDetailsToggle.querySelector("summary");
    // Use capture so we run before any bubble-phase listeners that might close other menus
    summary.addEventListener("click", this.onSummaryClick.bind(this), {
      capture: true,
    });

    summary.addEventListener(
      "pointerdown",
      (event) => {
        const isThisOpen = this.mainDetailsToggle.hasAttribute("open");
        if (isThisOpen) return;

        const openMenus = Array.from(
          document.querySelectorAll(".mega-menu[open]")
        ).filter((menu) => menu !== this.mainDetailsToggle);

        if (openMenus.length === 0) return;

        openMenus.forEach((menu) => menu.classList.add("menu-no-animation"));
        this.mainDetailsToggle.classList.add("menu-no-animation");
      },
      { capture: true }
    );

    // Add click listener to overlay to close menu
    const overlay = this.mainDetailsToggle.querySelector(".mega-menu__overlay");
    if (overlay) {
      overlay.addEventListener("click", this.onOverlayClick.bind(this));
    }

    // Listen for clicks on submenu links to re-enforce scroll lock
    this.addEventListener("click", (e) => {
      if (this.isMenuOpen && e.target.closest(".mega-menu__link--level-2")) {
        // Re-enforce scroll lock after a brief delay to let DOM update
        setTimeout(() => this.enforceScrollLock(), 10);
      }
    });

    // Debug helpers (enabled when window.DEBUG_MEGA_MENU = true)
    this.mmLog = (...args) => {
      if (window.DEBUG_MEGA_MENU) {
        console.log("[MegaMenu]", ...args);
      }
    };

    this.logMenuState = (menu, label = "state") => {
      if (!window.DEBUG_MEGA_MENU || !menu) return;
      const content = menu.querySelector(".mega-menu__content");
      const drawer = menu.querySelector(
        ".mega-menu__content > .content-drawer"
      );
      const overlay = menu.querySelector(
        ".mega-menu__content > .mega-menu__overlay"
      );
      const panel = menu.querySelector(
        ".mega-menu__content > .mega-menu__panel"
      );
      const panelInner = menu.querySelector(".mega-menu__panel-inner");
      const info = {
        id: menu.id,
        open: menu.hasAttribute("open"),
        noAnim: menu.classList.contains("menu-no-animation"),
        opening: menu.classList.contains("menu-opening"),
        anims: {
          content: content?.getAnimations?.().length || 0,
          drawer: drawer?.getAnimations?.().length || 0,
          overlay: overlay?.getAnimations?.().length || 0,
          panel: panel?.getAnimations?.().length || 0,
          panelInner: panelInner?.getAnimations?.().length || 0,
        },
      };
      this.mmLog(label, info);
    };
  }

  onSummaryClick(event) {
    event.preventDefault(); // Prevent default details toggle
    // Ensure no other click listeners on the same target run before our logic
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    } else {
      event.stopPropagation();
    }

    if (this.mainDetailsToggle.hasAttribute("open")) {
      // Currently open, start closing animation
      this.shouldAnimateClose = true;
      this.animateClose();
    } else {
      // Determine if another mega menu is already open → switching state
      const openMenus = Array.from(
        document.querySelectorAll(".mega-menu[open]")
      ).filter((menu) => menu !== this.mainDetailsToggle);

      if (openMenus.length > 0) {
        // Switching between menus: open instantly without animations
        this.openWithoutAnimation(openMenus);
      } else {
        // First open: keep animations
        this.animateOpen();
      }
    }
  }

  onOverlayClick(event) {
    event.preventDefault();
    event.stopPropagation();
    // Close the menu when overlay is clicked
    this.shouldAnimateClose = true;
    this.animateClose();
  }

  animateOpen() {
    this.isMenuOpen = true;

    // Only set as active scroll manager if there isn't one already
    if (!HeaderMenu.activeScrollManager) {
      HeaderMenu.activeScrollManager = this;

      // Store current scroll position BEFORE any DOM changes
      this.scrollPosition = window.pageYOffset || window.scrollY;
      console.log("[Menu Open] Saved scroll position:", this.scrollPosition);

      if (window.scrollTrigger) {
        window.scrollTrigger.disable();
      }

      const useBodyScrollLock =
        typeof window !== "undefined" &&
        window.bodyScrollLock &&
        typeof window.bodyScrollLock.lock === "function" &&
        typeof window.bodyScrollLock.unlock === "function";

      if (useBodyScrollLock) {
        this.scrollLockBreakpoint =
          window.matchMedia && window.matchMedia("(min-width: 990px)").matches
            ? "desktop"
            : "tablet";
        window.bodyScrollLock.lock(this.scrollLockBreakpoint);
      } else {
        // Calculate scrollbar width to prevent layout shift
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;

        // Store original styles
        this.originalBodyPosition = document.body.style.position;
        this.originalBodyTop = document.body.style.top;
        this.originalBodyWidth = document.body.style.width;
        this.originalBodyHeight = document.body.style.height;
        this.originalBodyPaddingRight = document.body.style.paddingRight;

        // Lock scrolling using position: fixed (works better on mobile Safari)
        document.body.style.position = "fixed";
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.style.width = "100%";
        document.body.style.height = "100%"; // Prevent height changes

        // Compensate for scrollbar to prevent layout shift
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        // Add touch event handling to prevent body scrolling on mobile
        this.touchMoveHandler = (e) => {
          // Allow scrolling only within the menu panel
          if (!e.target.closest(".mega-menu__panel")) {
            e.preventDefault();
          }
        };
        document.addEventListener("touchmove", this.touchMoveHandler, {
          passive: false,
        });
      }
    }

    this.mainDetailsToggle.setAttribute("open", "");
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", "true");
    this.mainDetailsToggle.setAttribute("aria-expanded", "true");
    this.setAttribute("aria-expanded", "true");
  }

  openWithoutAnimation(otherOpenMenus = []) {
    this.isMenuOpen = true;

    const previousManager = HeaderMenu.activeScrollManager;
    if (previousManager && previousManager !== this) {
      this.scrollPosition = previousManager.scrollPosition;
      this.scrollLockBreakpoint = previousManager.scrollLockBreakpoint;
      HeaderMenu.activeScrollManager = this;
      previousManager.isMenuOpen = false;
    } else if (!HeaderMenu.activeScrollManager) {
      if (window.ScrollTrigger) {
        window.ScrollTrigger.disable();
      }
    }
    const cancelAnimations = (root) => {
      if (!root) return;
      const parts = [
        root.querySelector(".mega-menu__content"),
        root.querySelector(".mega-menu__content > .content-drawer"),
        root.querySelector(".mega-menu__content > .mega-menu__overlay"),
        root.querySelector(".mega-menu__content > .mega-menu__panel"),
        root.querySelector(".mega-menu__panel-inner"),
      ].filter(Boolean);
      parts.forEach((el) => {
        try {
          el.getAnimations?.().forEach((anim) => anim.cancel());
          // Force-stop any CSS animations for this frame
          const prev = el.style.animation;
          el.style.animation = "none";
          // Hold suppression briefly to avoid first-switch flicker
          setTimeout(() => {
            el.style.animation = prev || "";
          }, 80);
        } catch (_) {}
      });
    };

    const applyInstantStyles = (root) => {
      if (!root) return;
      const content = root.querySelector(".mega-menu__content");
      const drawer = root.querySelector(
        ".mega-menu__content > .content-drawer"
      );
      const overlay = root.querySelector(
        ".mega-menu__content > .mega-menu__overlay"
      );
      const panel = root.querySelector(
        ".mega-menu__content > .mega-menu__panel"
      );
      const panelInner = root.querySelector(".mega-menu__panel-inner");

      if (content) {
        content.style.transition = "none";
        content.style.opacity = "1";
      }
      if (drawer) {
        drawer.style.transition = "none";
        drawer.style.animation = "none";
        drawer.style.transform = "translateX(0)";
      }
      if (overlay) {
        overlay.style.transition = "none";
        overlay.style.animation = "none";
        overlay.style.opacity = "0.6";
      }
      if (panel) {
        panel.style.transition = "none";
        panel.style.animation = "none";
        panel.style.opacity = "1";
      }
      if (panelInner) {
        panelInner.style.transition = "none";
        panelInner.style.animation = "none";
        panelInner.style.opacity = "1";
        panelInner.style.transform = "none";
      }
    };

    const clearInstantStyles = (root) => {
      if (!root) return;
      const content = root.querySelector(".mega-menu__content");
      const drawer = root.querySelector(
        ".mega-menu__content > .content-drawer"
      );
      const overlay = root.querySelector(
        ".mega-menu__content > .mega-menu__overlay"
      );
      const panel = root.querySelector(
        ".mega-menu__content > .mega-menu__panel"
      );
      const panelInner = root.querySelector(".mega-menu__panel-inner");

      if (content) {
        content.style.transition = "";
        content.style.opacity = "";
      }
      if (drawer) {
        drawer.style.transition = "";
        drawer.style.animation = "";
        drawer.style.transform = "";
      }
      if (overlay) {
        overlay.style.transition = "";
        overlay.style.animation = "";
        overlay.style.opacity = "";
      }
      if (panel) {
        panel.style.transition = "";
        panel.style.animation = "";
        panel.style.opacity = "";
      }
      if (panelInner) {
        panelInner.style.transition = "";
        panelInner.style.animation = "";
        panelInner.style.opacity = "";
        panelInner.style.transform = "";
      }
    };

    // Enter global switching state for the duration of this first switch
    // Force visual states on both menus to prevent any jank on first switch
    const forceVisible = (menu) => menu.classList.add("menu-force-visible");
    const forceHidden = (menu) => menu.classList.add("menu-force-hidden");
    const clearForces = (menu) => {
      menu.classList.remove("menu-force-visible");
      menu.classList.remove("menu-force-hidden");
    };

    otherOpenMenus.forEach((menu) => {
      menu.classList.add("menu-no-animation");
      menu.classList.remove("menu-opening");
      cancelAnimations(menu);
      applyInstantStyles(menu);
      // Hide immediately to avoid one-frame visibility during attribute removal
      forceHidden(menu);
      const summary = menu.querySelector("summary");
      menu.removeAttribute("open");
      summary && summary.setAttribute("aria-expanded", "false");
      menu.setAttribute("aria-expanded", "false");
      // Also update the header-menu custom element
      const headerMenuElement = menu.closest("header-menu");
      if (headerMenuElement) {
        headerMenuElement.setAttribute("aria-expanded", "false");
      }
      // Clear panel content to avoid any late paint
      const panel = menu.querySelector("[data-mega-panel]");
      if (panel) panel.innerHTML = "";
      this.logMenuState(menu, "closed-prev");
    });

    // Open this menu instantly without animations, but on next frame to avoid overlap
    requestAnimationFrame(() => {
      this.mainDetailsToggle.classList.add("menu-no-animation");
      this.mainDetailsToggle.classList.remove("menu-opening");
      cancelAnimations(this.mainDetailsToggle);
      applyInstantStyles(this.mainDetailsToggle);
      forceVisible(this.mainDetailsToggle);
      this.mainDetailsToggle.setAttribute("open", "");
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", "true");
      this.mainDetailsToggle.setAttribute("aria-expanded", "true");
      this.setAttribute("aria-expanded", "true");
      this.logMenuState(this.mainDetailsToggle, "opened-current-noanim");
    });

    // Ensure the previous menu loses the no-animation class after switch to avoid persisting state
    requestAnimationFrame(() => {
      otherOpenMenus.forEach((menu) => {
        menu.classList.remove("menu-no-animation");
        clearForces(menu);
        clearInstantStyles(menu);
      });
      clearForces(this.mainDetailsToggle);
      clearInstantStyles(this.mainDetailsToggle);
    });
  }

  enforceScrollLock() {
    if (!this.isMenuOpen || this.scrollPosition === undefined) return;

    // Re-apply scroll prevention using position: fixed (better mobile compatibility)
    // document.body.style.position = "fixed";
    // document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = "100%";
    document.body.style.height = "100%";

    // Re-apply scrollbar compensation if needed
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  animateClose() {
    if (this.isAnimatingClose) return;

    this.isAnimatingClose = true;
    this.isMenuOpen = false;

    if (this.shouldAnimateClose) {
      this.mainDetailsToggle.classList.remove("menu-no-animation");

      document.querySelectorAll(".header__menu-item").forEach((item) => {
        item.style.color = "var(--color-secondary)";
      });
    }
    this.mainDetailsToggle.classList.add("menu-opening");

    setTimeout(() => {
      this.mainDetailsToggle.removeAttribute("open");
      this.mainDetailsToggle.classList.remove("menu-opening");
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", "false");
      this.mainDetailsToggle.setAttribute("aria-expanded", "false");
      this.setAttribute("aria-expanded", "false");
      this.isAnimatingClose = false;
      this.shouldAnimateClose = false;

      // Reset any no-animation state when fully closed
      if (!document.querySelector(".mega-menu[open]")) {
        document
          .querySelectorAll(".mega-menu.menu-no-animation")
          .forEach((el) => el.classList.remove("menu-no-animation"));
      }

      // Only restore body state if THIS instance is the active scroll manager
      if (HeaderMenu.activeScrollManager === this) {
        const useBodyScrollLock =
          typeof window !== "undefined" &&
          window.bodyScrollLock &&
          typeof window.bodyScrollLock.unlock === "function";

        if (useBodyScrollLock && this.scrollLockBreakpoint) {
          window.bodyScrollLock.unlock(this.scrollLockBreakpoint);
        } else {
          // Restore original styles
          document.body.style.position = this.originalBodyPosition || "";
          document.body.style.top = this.originalBodyTop || "";
          document.body.style.width = this.originalBodyWidth || "";
          document.body.style.height = this.originalBodyHeight || "";
          document.body.style.paddingRight =
            this.originalBodyPaddingRight || "";

          // Remove touch event handler if it was added
          if (this.touchMoveHandler) {
            document.removeEventListener("touchmove", this.touchMoveHandler);
            this.touchMoveHandler = null;
          }
        }

        window.scrollTo(0, this.scrollPosition);

        if (window.ScrollTrigger) {
          requestAnimationFrame(() => {
            window.ScrollTrigger.enable();
            window.ScrollTrigger.refresh();
          });
        }

        this.scrollLockBreakpoint = null;

        // Clear the active scroll manager
        HeaderMenu.activeScrollManager = null;
      }
    }, 400);
  }

  onToggle() {
    // Don't call parent's onToggle as we're handling this manually
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop"
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  close() {
    // Override close to use our animated close
    this.animateClose();
  }
}

customElements.define("header-menu", HeaderMenu);
